import * as React from 'react'
import clsx from 'clsx'
import { TypographyProps, Typography } from '../Typography'
import { SxProps, sx as sxFunc } from '../sx'
import { useFormControl, formControlState } from '../FormControl'
import style from './index.module.scss'
import { capitalize } from '../../utils'

export interface FormControlLabelClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `labelPlacement="start"`. */
  labelPlacementStart: string
  /** Styles applied to the root element if `labelPlacement="top"`. */
  labelPlacementTop: string
  /** Styles applied to the root element if `labelPlacement="bottom"`. */
  labelPlacementBottom: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the label's Typography component. */
  label: string
  /** State class applied to the root element if `error={true}`. */
  error: string
}

export interface FormControlLabelProps
  extends Omit<
    React.LabelHTMLAttributes<HTMLLabelElement>,
    'children' | 'onChange'
  > {
  /**
   * If `true`, the component appears selected.
   */
  checked?: boolean
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<FormControlLabelClasses>
  /**
   * The props used for each slot inside.
   * @default {}
   */
  componentsProps?: {
    /**
     * Props applied to the Typography wrapper of the passed label.
     * This is unused if disableTypography is true.
     * @default {}
     */
    typography?: TypographyProps
  }
  /**
   * A control element. For instance, it can be a `Radio`, a `Switch` or a `Checkbox`.
   */
  control: React.ReactElement<any, any>
  /**
   * If `true`, the control is disabled.
   */
  disabled?: boolean
  /**
   * If `true`, the label is rendered as it is passed without an additional typography node.
   */
  disableTypography?: boolean
  /**
   * Pass a ref to the `input` element.
   */
  inputRef?: React.Ref<any>
  /**
   * A text or an element to be used in an enclosing label element.
   */
  label: React.ReactNode
  /**
   * The position of the label.
   * @default 'end'
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom'
  name?: string
  /**
   * Callback fired when the state is changed.
   *
   * @param {React.SyntheticEvent} event The event source of the callback.
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange?: (event: React.SyntheticEvent, checked: boolean) => void
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: {
    /**
     * Props applied to the Typography wrapper of the passed label.
     * This is unused if disableTypography is true.
     * @default {}
     */
    typography?: TypographyProps
  }
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The value of the component.
   */
  value?: unknown
}

/**
 * Drop-in replacement of the `Radio`, `Switch` and `Checkbox` component.
 * Use this component if you want to display an extra label.
 */

/* eslint-disable @typescript-eslint/no-unused-vars */
export const FormControlLabel = React.forwardRef<
  HTMLLabelElement,
  FormControlLabelProps
>(function FormControlLabel(props, ref) {
  const {
    checked,
    classes,
    className,
    componentsProps = {},
    control,
    disabled: disabledProp,
    disableTypography,
    inputRef,
    label: labelProp,
    labelPlacement = 'end',
    name,
    onChange,
    slotProps = {},
    value,
    sx,
    ...other
  } = props

  const factorFormControl = useFormControl()

  let disabled = disabledProp
  if (
    typeof disabled === 'undefined' &&
    typeof control.props.disabled !== 'undefined'
  ) {
    disabled = control.props.disabled
  }
  if (typeof disabled === 'undefined' && factorFormControl) {
    disabled = factorFormControl.disabled
  }

  const controlProps: any = {
    disabled,
  }

  ;(['checked', 'name', 'onChange', 'value', 'inputRef'] as const).forEach(
    key => {
      if (
        typeof control.props[key] === 'undefined' &&
        typeof props[key] !== 'undefined'
      ) {
        controlProps[key] = props[key]
      }
    }
  )

  const fcs = formControlState({
    props,
    factorFormControl,
    states: ['error'],
  })

  const typographySlotProps = slotProps.typography ?? componentsProps.typography

  let label = labelProp
  if (
    label != null &&
    (label as any).type !== Typography &&
    !disableTypography
  ) {
    label = (
      <Typography
        component="span"
        {...typographySlotProps}
        gutterBottom={false}
        className={clsx(
          style.label,
          classes?.label,
          typographySlotProps?.className
        )}
      >
        {label}
      </Typography>
    )
  }

  const controlSize = control?.props?.size ?? 'medium'

  return (
    <label
      className={clsx(
        classes?.root,
        style.root,
        disabled && [style.disabled, classes?.disabled],
        // @ts-ignore
        controlSize && style[`size--${controlSize}`],
        [
          // @ts-ignore
          style[`labelPlacement-${labelPlacement}`],
          // @ts-ignore
          classes?.[`labelPlacement${capitalize(labelPlacement)}`],
        ],
        fcs.error && [classes?.error],
        className,
        sxFunc(sx)
      )}
      ref={ref}
      {...other}
    >
      {React.cloneElement(control, controlProps)}
      {label}
    </label>
  )
})
